import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../../styles/GoldenYears.css';

const GoldenYears = ({ isPreview = true }) => {
  const { coupleNames } = useParams();
  const [isRevealed, setIsRevealed] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [rsvpData, setRsvpData] = useState({
    name: '',
    email: '',
    attending: '',
    guests: '1',
    message: ''
  });

  // Parse couple names
  const formatNames = (names) => {
    if (!names) return { name1: 'Antonio', name2: 'Elena' };
    const parts = names.split('-and-');
    if (parts.length === 2) {
      return {
        name1: parts[0].split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' '),
        name2: parts[1].split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ')
      };
    }
    return { name1: 'Antonio', name2: 'Elena' };
  };

  const names = formatNames(coupleNames);

  const invitationData = {
    husband: names.name1,
    wife: names.name2,
    yearsMarried: '50',
    weddingDate: 'June 20, 1976',
    celebrationDate: 'June 20, 2026',
    celebrationDay: 'Saturday',
    celebrationTime: '4:00 PM',
    venue: 'Taal Vista Hotel',
    venueAddress: 'Tagaytay City, Cavite',
    message: 'Fifty golden years of love, devotion, and endless blessings. Join us in celebrating this extraordinary milestone.',
    memories: [
      { decade: '1976-1986', title: 'The Beginning', desc: 'Building our home & family' },
      { decade: '1986-1996', title: 'Growing Together', desc: 'Raising our children' },
      { decade: '1996-2006', title: 'New Adventures', desc: 'Welcoming grandchildren' },
      { decade: '2006-2016', title: 'Golden Moments', desc: 'Cherishing every day' },
      { decade: '2016-2026', title: 'Forever Blessed', desc: 'Still going strong' }
    ],
    hostedBy: 'The Reyes Children & Grandchildren'
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsRevealed(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="golden-years">
      {/* Decorative Elements */}
      <div className="golden-elements">
        <div className="golden-shimmer"></div>
        <div className="golden-rays"></div>
        <div className="corner-flourish top-left">❧</div>
        <div className="corner-flourish top-right">☙</div>
        <div className="corner-flourish bottom-left">☙</div>
        <div className="corner-flourish bottom-right">❧</div>
        <div className="floating-star s1">✦</div>
        <div className="floating-star s2">✦</div>
        <div className="floating-star s3">✦</div>
      </div>

      {/* Main Content */}
      <div className={`golden-container ${isRevealed ? 'revealed' : ''}`}>
        {/* Navigation */}
        <nav className="golden-nav">
          <button 
            className={activeSection === 'home' ? 'active' : ''} 
            onClick={() => setActiveSection('home')}
          >
            Invitation
          </button>
          <button 
            className={activeSection === 'memories' ? 'active' : ''} 
            onClick={() => setActiveSection('memories')}
          >
            Memories
          </button>
          <button 
            className={activeSection === 'rsvp' ? 'active' : ''} 
            onClick={() => setActiveSection('rsvp')}
          >
            RSVP
          </button>
        </nav>

        {/* Home Section */}
        {activeSection === 'home' && (
          <section className="golden-section home-section">
            <div className="golden-crest">
              <span className="crest-top">✦</span>
              <div className="anniversary-number">
                <span className="number">50</span>
                <span className="ordinal">th</span>
              </div>
              <span className="crest-label">Golden Anniversary</span>
            </div>

            <p className="invitation-prefix">Celebrating</p>

            <div className="couple-names">
              <span className="name husband">{invitationData.husband}</span>
              <span className="ampersand">&</span>
              <span className="name wife">{invitationData.wife}</span>
            </div>

            <div className="golden-divider">
              <span className="divider-wing">═══</span>
              <span className="divider-gem">◆</span>
              <span className="divider-wing">═══</span>
            </div>

            <p className="invitation-message">{invitationData.message}</p>

            <div className="date-showcase">
              <div className="date-then">
                <span className="date-label">Married</span>
                <span className="date-value">{invitationData.weddingDate}</span>
              </div>
              <div className="date-arrow">→</div>
              <div className="date-now">
                <span className="date-label">Celebrating</span>
                <span className="date-value">{invitationData.celebrationDate}</span>
              </div>
            </div>

            <div className="event-info-box">
              <div className="info-row">
                <span className="info-icon">🕓</span>
                <span className="info-text">{invitationData.celebrationTime}</span>
              </div>
              <div className="info-row">
                <span className="info-icon">📍</span>
                <div className="info-venue">
                  <span className="venue-name">{invitationData.venue}</span>
                  <span className="venue-address">{invitationData.venueAddress}</span>
                </div>
              </div>
            </div>

            <button 
              className="golden-btn"
              onClick={() => setActiveSection('rsvp')}
            >
              Join Our Celebration
            </button>
          </section>
        )}

        {/* Memories Section */}
        {activeSection === 'memories' && (
          <section className="golden-section memories-section">
            <h2 className="section-title">
              <span className="title-star">✦</span>
              50 Years of Love
              <span className="title-star">✦</span>
            </h2>

            <div className="decades-timeline">
              {invitationData.memories.map((memory, index) => (
                <div key={index} className="decade-card">
                  <div className="decade-header">
                    <span className="decade-years">{memory.decade}</span>
                  </div>
                  <div className="decade-content">
                    <span className="decade-title">{memory.title}</span>
                    <span className="decade-desc">{memory.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="golden-quote">
              <span className="quote-icon">❝</span>
              <p className="quote-text">
                Grow old along with me, the best is yet to be.
              </p>
              <span className="quote-author">— Robert Browning</span>
            </div>

            <div className="hosted-section">
              <span className="hosted-label">This celebration is lovingly hosted by</span>
              <span className="hosted-name">{invitationData.hostedBy}</span>
            </div>

            <button 
              className="golden-btn"
              onClick={() => setActiveSection('rsvp')}
            >
              RSVP Now
            </button>
          </section>
        )}

        {/* RSVP Section */}
        {activeSection === 'rsvp' && (
          <section className="golden-section rsvp-section">
            <h2 className="section-title">
              <span className="title-star">✦</span>
              RSVP
              <span className="title-star">✦</span>
            </h2>

            <form className="golden-rsvp-form" onSubmit={(e) => e.preventDefault()}>
              <p className="rsvp-intro">Your presence would mean the world to us</p>
              <p className="rsvp-deadline">Please respond by June 1, 2026</p>

              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  name="name"
                  value={rsvpData.name}
                  placeholder="Enter your full name"
                  disabled={isPreview}
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={rsvpData.email}
                  placeholder="your.email@example.com"
                  disabled={isPreview}
                />
              </div>

              <div className="form-group">
                <label>Will you be joining us?</label>
                <div className="attendance-options">
                  <label className={`attendance-option ${isPreview ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="attending"
                      value="yes"
                      disabled={isPreview}
                    />
                    <span className="option-gem">◆</span>
                    <span className="option-text">Happily Accepts</span>
                  </label>
                  <label className={`attendance-option ${isPreview ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="attending"
                      value="no"
                      disabled={isPreview}
                    />
                    <span className="option-gem">◇</span>
                    <span className="option-text">Regretfully Declines</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Number of Guests</label>
                <select
                  name="guests"
                  value={rsvpData.guests}
                  disabled={isPreview}
                >
                  <option value="1">1 Guest</option>
                  <option value="2">2 Guests</option>
                  <option value="3">3 Guests</option>
                  <option value="4">4 Guests</option>
                  <option value="5">5 Guests</option>
                </select>
              </div>

              <div className="form-group">
                <label>Message for the Couple (Optional)</label>
                <textarea
                  name="message"
                  value={rsvpData.message}
                  placeholder="Share your love and wishes..."
                  rows="3"
                  disabled={isPreview}
                />
              </div>

              <button type="submit" className="golden-btn full-width" disabled={isPreview}>
                Send RSVP
              </button>
            </form>
          </section>
        )}

        {/* Watermark */}
        <div className="golden-watermark">
          <a href="/invitations" className="back-to-templates-btn">← Back to Templates</a>
          <p>Crafted with love by <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer">Logica Technology</a></p>
        </div>
      </div>
    </div>
  );
};

export default GoldenYears;
